/**
 * The console's information architecture, as DATA.
 *
 * Ten tabs in a horizontally-scrolling strip hid tabs 6-10 behind a scrollbar with no
 * affordance, and thirteen would be worse. They regroup behind four named sections, and the
 * section a screen belongs to lives here rather than in the filesystem — a re-group should
 * be one line, not a `git mv`.
 *
 * No `icon` field, deliberately. `GlobalDataView.jsx` is the repo's 12-tab precedent and it
 * uses icon pills; this console reads as a ledger, where the type and the rules carry the
 * structure. That is a departure by choice, not by omission.
 */

export const SECTIONS = ["money", "books", "policy", "operations"];

/**
 * Every screen. `key` is what appears in `?screen=`, so it is a stable URL contract.
 * `labelKey` resolves in the `superadmin` namespace.
 */
export const SCREENS = [
  // ── Money: what is owed, paid, refunded, disputed ────────────────────────
  { key: "payouts", section: "money", labelKey: "gw.console.screen.payouts" },
  { key: "payments", section: "money", labelKey: "gw.console.screen.payments" },
  { key: "disputes", section: "money", labelKey: "gw.console.screen.disputes" },
  { key: "adjustments", section: "money", labelKey: "gw.console.screen.adjustments" },
  // Money that WAS computed and never captured — a declined card, an expired hold. Real
  // money sitting invisible until a human looks, so it belongs with the money, not with the
  // machinery.
  { key: "awaitingOperator", section: "money", labelKey: "gw.console.screen.awaitingOperator" },

  // ── Books: do the numbers agree with each other and with Stripe ──────────
  { key: "balances", section: "books", labelKey: "gw.console.screen.balances" },
  { key: "reconciliation", section: "books", labelKey: "gw.console.screen.reconciliation" },
  { key: "books", section: "books", labelKey: "gw.console.screen.books" },

  // ── Policy: the rules that decide future money ───────────────────────────
  { key: "commission", section: "policy", labelKey: "gw.console.screen.commission" },
  { key: "consultRates", section: "policy", labelKey: "gw.console.screen.consultRates" },
  { key: "rules", section: "policy", labelKey: "gw.console.screen.rules" },

  // ── Operations: is the machinery healthy ─────────────────────────────────
  // Dispute RATES sits here, not in Money. The Disputes screen answers "who bears this
  // loss"; this one answers "is Stripe about to close our platform account" — a different
  // question with a different reader, and it belongs beside the other health screens.
  { key: "disputeRates", section: "operations", labelKey: "gw.console.screen.disputeRates" },
  { key: "events", section: "operations", labelKey: "gw.console.screen.events" },
  { key: "consults", section: "operations", labelKey: "gw.console.screen.consults" },
];

export const screenById = new Map(SCREENS.map((s) => [s.key, s]));

export const screensIn = (section) => SCREENS.filter((s) => s.section === section);

/** Clicking a section name lands on its most-used screen. */
export const DEFAULT_SCREEN = {
  money: "payouts",
  books: "balances",
  policy: "commission",
  operations: "disputeRates",
};

/** The section a screen belongs to is DERIVED, never carried in the URL — two fields that
 *  can disagree eventually will. */
export const sectionOf = (screenKey) => screenById.get(screenKey)?.section || null;
